<template>
  <div class="browser-info-detector">
    <div class="info-card">
      <div class="info-section">
        <h3>{{ t('browserInfo.sections.browser.title') }}</h3>
        <div class="info-item">
          <div class="info-label">{{ t('browserInfo.sections.browser.fingerprint') }}:</div>
          <div class="info-value">{{ browserFingerprint }}</div>
        </div>
        <div class="info-item">
          <div class="info-label">{{ t('browserInfo.sections.browser.name') }}:</div>
          <div class="info-value">{{ browserInfo.name }}</div>
        </div>
        <div class="info-item">
          <div class="info-label">{{ t('browserInfo.sections.browser.version') }}:</div>
          <div class="info-value">{{ browserInfo.version }}</div>
        </div>
        <div class="info-item">
          <div class="info-label">{{ t('browserInfo.sections.browser.userAgent') }}:</div>
          <div class="info-value user-agent">{{ browserInfo.userAgent }}</div>
        </div>
      </div>

      <div class="info-section">
        <h3>{{ t('browserInfo.sections.features.title') }}</h3>
        <div class="info-item">
          <div class="info-label">{{ t('browserInfo.sections.features.javascript') }}:</div>
          <div class="info-value">
            <span class="status-indicator" :class="{ 'enabled': features.javascript }"></span>
            {{ features.javascript ? t('browserInfo.sections.features.enabled') : t('browserInfo.sections.features.disabled') }}
          </div>
        </div>
        <div class="info-item">
          <div class="info-label">{{ t('browserInfo.sections.features.java') }}:</div>
          <div class="info-value">
            <span class="status-indicator" :class="{ 'enabled': features.java }"></span>
            {{ features.java ? t('browserInfo.sections.features.enabled') : t('browserInfo.sections.features.disabled') }}
          </div>
        </div>
        <div class="info-item">
          <div class="info-label">{{ t('browserInfo.sections.features.cookies') }}:</div>
          <div class="info-value">
            <span class="status-indicator" :class="{ 'enabled': features.cookies }"></span>
            {{ features.cookies ? t('browserInfo.sections.features.enabled') : t('browserInfo.sections.features.disabled') }}
          </div>
        </div>
      </div>

      <div class="info-section">
        <h3>{{ t('browserInfo.sections.time.title') }}</h3>
        <div class="info-item">
          <div class="info-label">{{ t('browserInfo.sections.time.timezone') }}:</div>
          <div class="info-value">{{ timeInfo.timezone }}</div>
        </div>
        <div class="info-item">
          <div class="info-label">{{ t('browserInfo.sections.time.localTime') }}:</div>
          <div class="info-value">{{ timeInfo.localTime }}</div>
        </div>
        <div class="info-item">
          <div class="info-label">{{ t('browserInfo.sections.time.systemTime') }}:</div>
          <div class="info-value">{{ timeInfo.systemTime }}</div>
        </div>
      </div>

      <div class="info-section">
        <h3>{{ t('browserInfo.sections.system.title') }}</h3>
        <div class="info-item">
          <div class="info-label">{{ t('browserInfo.sections.system.os') }}:</div>
          <div class="info-value">{{ systemInfo.os }}</div>
        </div>
        <div class="info-item">
          <div class="info-label">{{ t('browserInfo.sections.system.deviceType') }}:</div>
          <div class="info-value">{{ systemInfo.deviceType }}</div>
        </div>
        <div class="info-item">
          <div class="info-label">{{ t('browserInfo.sections.system.screenResolution') }}:</div>
          <div class="info-value">{{ systemInfo.screenResolution }}</div>
        </div>
      </div>
    </div>
    <!-- <button @click="refreshInfo" class="refresh-btn">{{ t('browserInfo.refresh') }}</button> -->
  </div>
</template>

<script>
import { useI18n } from 'vue-i18n';

export default {
  name: 'BrowserInfoDetector',
  setup() {
    const { t } = useI18n();
    return { t };
  },
  data() {
    return {
      browserFingerprint: '',
      browserInfo: {
        name: '',
        version: '',
        userAgent: ''
      },
      features: {
        javascript: true,
        java: false,
        cookies: false
      },
      timeInfo: {
        timezone: '',
        localTime: '',
        systemTime: ''
      },
      systemInfo: {
        os: '',
        deviceType: '',
        screenResolution: ''
      }
    }
  },
  mounted() {
    this.detectBrowserInfo();
    this.detectFeatures();
    this.detectTimeInfo();
    this.detectSystemInfo();
    this.generateBrowserFingerprint();
    
    // 更新时间
    this.timeInterval = setInterval(() => {
      this.updateTime();
    }, 1000);
  },
  beforeUnmount() {
    if (this.timeInterval) {
      clearInterval(this.timeInterval);
    }
  },
  methods: {
    detectBrowserInfo() {
      const ua = navigator.userAgent;
      this.browserInfo.userAgent = ua;
      
      // 检测浏览器名称和版本
      if (ua.includes('Firefox/')) {
        this.browserInfo.name = 'Firefox';
        this.browserInfo.version = ua.match(/Firefox\/([\d.]+)/)[1];
      } else if (ua.includes('Edg/')) {
        this.browserInfo.name = 'Microsoft Edge';
        this.browserInfo.version = ua.match(/Edg\/([\d.]+)/)[1];
      } else if (ua.includes('Chrome/')) {
        this.browserInfo.name = 'Chrome';
        this.browserInfo.version = ua.match(/Chrome\/([\d.]+)/)[1];
      } else if (ua.includes('Safari/') && !ua.includes('Chrome/')) {
        this.browserInfo.name = 'Safari';
        this.browserInfo.version = ua.match(/Version\/([\d.]+)/)?.[1] || '未知';
      } else if (ua.includes('MSIE ') || ua.includes('Trident/')) {
        this.browserInfo.name = 'Internet Explorer';
        this.browserInfo.version = ua.match(/MSIE ([\d.]+)/) ? 
          ua.match(/MSIE ([\d.]+)/)[1] : 
          ua.match(/rv:([\d.]+)/)[1];
      } else {
        this.browserInfo.name = '未知浏览器';
        this.browserInfo.version = '未知';
      }
    },
    
    detectFeatures() {
      // JavaScript 总是启用的，因为这段代码正在运行
      this.features.javascript = true;
      
      // 检测 Java
      this.features.java = navigator.javaEnabled ? navigator.javaEnabled() : false;
      
      // 检测 Cookies
      this.features.cookies = navigator.cookieEnabled;
    },
    
    detectTimeInfo() {
      this.updateTime();
    },
    
    updateTime() {
      const now = new Date();
      
      // 时区
      this.timeInfo.timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      
      // 当地时间
      this.timeInfo.localTime = now.toLocaleString();
      
      // 系统时间
      this.timeInfo.systemTime = now.toString();
    },
    
    detectSystemInfo() {
      const ua = navigator.userAgent;
      
      // 检测操作系统
      if (ua.includes('Windows NT')) {
        const version = ua.match(/Windows NT ([\d.]+)/)[1];
        let osName = 'Windows';
        
        switch(version) {
          case '10.0': osName = 'Windows 10'; break;
          case '6.3': osName = 'Windows 8.1'; break;
          case '6.2': osName = 'Windows 8'; break;
          case '6.1': osName = 'Windows 7'; break;
          case '6.0': osName = 'Windows Vista'; break;
          case '5.2': osName = 'Windows XP 64-bit'; break;
          case '5.1': osName = 'Windows XP'; break;
          default: osName = `Windows (NT ${version})`;
        }
        
        this.systemInfo.os = osName;
      } else if (ua.includes('Mac OS X')) {
        let version = ua.match(/Mac OS X ([^)]+)/);
        version = version ? version[1].replace(/_/g, '.') : '';
        this.systemInfo.os = version ? `macOS ${version}` : 'macOS';
      } else if (ua.includes('Linux')) {
        this.systemInfo.os = 'Linux';
      } else if (ua.includes('Android')) {
        const version = ua.match(/Android ([\d.]+)/);
        this.systemInfo.os = version ? `Android ${version[1]}` : 'Android';
      } else if (ua.includes('iOS') || ua.includes('iPhone OS')) {
        const version = ua.match(/OS ([\d_]+)/);
        const formattedVersion = version ? version[1].replace(/_/g, '.') : '';
        this.systemInfo.os = formattedVersion ? `iOS ${formattedVersion}` : 'iOS';
      } else {
        this.systemInfo.os = this.t('browserInfo.sections.system.unknown');
      }
      
      // 检测设备类型
      if (ua.includes('Mobile') || ua.includes('iPhone') || ua.includes('iPad') || ua.includes('Android')) {
        if (ua.includes('iPad') || (ua.includes('Android') && !ua.includes('Mobile'))) {
          this.systemInfo.deviceType = this.t('browserInfo.sections.system.deviceTypes.tablet');
        } else {
          this.systemInfo.deviceType = this.t('browserInfo.sections.system.deviceTypes.mobile');
        }
      } else {
        this.systemInfo.deviceType = this.t('browserInfo.sections.system.deviceTypes.desktop');
      }
      
      // 屏幕分辨率
      this.systemInfo.screenResolution = `${window.screen.width} x ${window.screen.height}`;
    },
    
    generateBrowserFingerprint() {
      // 简单的浏览器指纹生成
      // 实际应用中可能会使用更复杂的算法和更多的参数
      const components = [
        navigator.userAgent,
        navigator.language,
        new Date().getTimezoneOffset(),
        navigator.platform,
        navigator.doNotTrack,
        window.screen.colorDepth,
        window.screen.width,
        window.screen.height
      ];
      
      // 使用简单的哈希函数
      let hash = 0;
      const str = components.join('');
      
      for (let i = 0; i < str.length; i++) {
        const char = str.charCodeAt(i);
        hash = ((hash << 5) - hash) + char;
        hash = hash & hash; // 转换为32位整数
      }
      
      // 转换为16进制字符串
      this.browserFingerprint = (hash >>> 0).toString(16).padStart(8, '0');
    },
    
    refreshInfo() {
      this.detectBrowserInfo();
      this.detectFeatures();
      this.detectTimeInfo();
      this.detectSystemInfo();
      this.generateBrowserFingerprint();
    }
  }
}
</script>

<style scoped>
.browser-info-detector {
  /* background-color: #f5f5f5;
  border-radius: 8px;
  padding: 15px; */
  margin-bottom: 15px;
  /* box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1); */
}

h2 {
  color: #1e3a8a;
  margin-bottom: 20px;
}

h3 {
  color: #2c5282;
  margin-top: 0;
  margin-bottom: 15px;
  font-size: 1.1em;
}

.info-card {
  background-color: white;
  border-radius: 8px;
  padding: 12px;
  margin-bottom: 12px;
}

.info-section {
  margin-bottom: 15px;
  padding-bottom: 12px;
  border-bottom: 1px solid #eee;
}

.info-section:last-child {
  border-bottom: none;
  margin-bottom: 0;
  padding-bottom: 0;
}

.info-item {
  display: flex;
  margin-bottom: 8px;
  padding: 6px;
  background-color: #f9f9f9;
  border-radius: 4px;
}

.info-label {
  font-weight: bold;
  width: 120px;
  color: #555;
}

.info-value {
  flex: 1;
}

.user-agent {
  word-break: break-all;
}

.status-indicator {
  display: inline-block;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  margin-right: 8px;
  background-color: #e53e3e;
}

.status-indicator.enabled {
  background-color: #38a169;
}

.refresh-btn {
  background-color: #1e3a8a;
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
}

.refresh-btn:hover {
  background-color: #2a4caf;
}
</style>
